/* eslint-disable @blumintinc/blumint/extract-global-constants */
import Box from '@mui/system/Box';
import { useState } from 'react';
import { AuthenticationNavbar } from './AuthenticationNavbar';
import { AuthenticationEmailSignInPage } from './flow-pages/AuthenticationEmailSignInPage';
import { AuthenticationEmailSignUpPage } from './flow-pages/AuthenticationEmailSignUpPage';
import { AuthenticationEnterConfirmationCodePage } from './flow-pages/AuthenticationEnterConfirmationCodePage';
import { AuthenticationEnterEmailPage } from './flow-pages/AuthenticationEnterEmailPage';
import { AuthenticationEnterPhonePage } from './flow-pages/AuthenticationEnterPhonePage';
import { AuthenticationFlowCompletePage } from './flow-pages/AuthenticationFlowCompletePage';
import { AuthenticationRegistrationStartPage } from './flow-pages/AuthenticationRegistrationStartPage';
import type { NonFunctionKeys } from 'utility-types';
import type { PageSpecificUserData } from './store/pageDataTypes';
import { AuthenticationVerifyingEmailPage } from './flow-pages/AuthenticationVerifyingEmailPage';
import { AuthenticationResetPasswordPage } from './flow-pages/AuthenticationResetPasswordPage';
import { AuthenticationResetPasswordSuccessPage } from './flow-pages/AuthenticationResetPasswordSuccessPage';
import { AuthenticationVerifyingEmailSuccessPage } from './flow-pages/AuthenticationVerifyingEmailSuccessPage';

export type AuthenticationFlowPageId = NonFunctionKeys<PageSpecificUserData>;

type PageIdData = {
  element: JSX.Element;
  prevPageId?: AuthenticationFlowPageId;
  nextPageId?: AuthenticationFlowPageId;
  currPageId?: AuthenticationFlowPageId;
};

// Make sure every flow page is wrapped in a single <div></div>!
export type AuthenticationFlowPageProps = {
  pageId: AuthenticationFlowPageId;
  onPageComplete: (
    pageId: AuthenticationFlowPageId,
    nextPageId?: AuthenticationFlowPageId,
  ) => void;
};

const ENTRY_POINT_DEFAULT: AuthenticationFlowPageId = 'registration start';
const AUTH_FLOW_END_POINT: AuthenticationFlowPageId | undefined =
  'registration end';

type AuthenticationFlowProps = {
  closeModal: () => void;
  startPage?: AuthenticationFlowPageId;
  startPageProps?: Record<string, unknown>;
};

export function AuthenticationFlow({
  closeModal,
  startPage = ENTRY_POINT_DEFAULT,
  startPageProps = {},
}: AuthenticationFlowProps) {
  const goBack = (currentPageData: PageIdData) => {
    if (!!currentPageData.prevPageId) {
      changePages(currentPageData.prevPageId);
    }
  };

  // Handles the necessary operations with the data collected across the entire auth flow
  const onAuthFlowComplete = () => {
    /*nothing*/
  };

  // Handles the changing of the pages after a page has declared it is done
  const onPageComplete = (
    pageId: AuthenticationFlowPageId,
    nextPageId:
      | AuthenticationFlowPageId
      | undefined = authenticationFlowPageIdMap[`${pageId}`].nextPageId,
  ) => {
    if (!!nextPageId) {
      changePages(nextPageId);
      if (nextPageId === AUTH_FLOW_END_POINT) {
        onAuthFlowComplete();
      }
    } else {
      onAuthFlowComplete();
      closeModal();
    }
  };

  const authenticationFlowPageIdMap: {
    [pageID in AuthenticationFlowPageId]: PageIdData;
  } = {
    'registration start': {
      element: (
        <AuthenticationRegistrationStartPage
          pageId={'registration start'}
          closeModal={closeModal}
          onPageComplete={onPageComplete}
        />
      ),
      currPageId: 'registration start',
    },
    'enter phone': {
      element: (
        <AuthenticationEnterPhonePage
          pageId={'enter phone'}
          onPageComplete={onPageComplete}
        />
      ),
      prevPageId: 'registration start',
      nextPageId: 'enter confirmation code',
    },
    'enter confirmation code': {
      element: (
        <AuthenticationEnterConfirmationCodePage
          pageId={'enter confirmation code'}
          onPageComplete={onPageComplete}
          closeModal={closeModal}
          {...startPageProps}
        />
      ),
      prevPageId: 'enter phone',
      nextPageId: 'registration end',
    },
    'email sign in': {
      element: (
        <AuthenticationEmailSignInPage
          pageId={'email sign in'}
          onPageComplete={onPageComplete}
        />
      ),
      prevPageId: 'enter email',
    },
    'email sign up': {
      element: (
        <AuthenticationEmailSignUpPage
          pageId={'email sign up'}
          onPageComplete={onPageComplete}
        />
      ),
      prevPageId: 'enter email',
      nextPageId: 'verifying email',
    },
    'enter email': {
      element: (
        <AuthenticationEnterEmailPage
          pageId={'enter email'}
          onPageComplete={onPageComplete}
        />
      ),
      prevPageId: 'registration start',
    },
    'verifying email': {
      element: (
        <AuthenticationVerifyingEmailPage
          pageId={'verifying email'}
          onPageComplete={onPageComplete}
        />
      ),
      nextPageId: 'verifying email success',
    },
    'verifying email success': {
      element: (
        <AuthenticationVerifyingEmailSuccessPage
          pageId={'verifying email success'}
          onPageComplete={onPageComplete}
        />
      ),
      prevPageId: 'verifying email',
    },
    'reset password': {
      element: (
        <AuthenticationResetPasswordPage
          pageId={'reset password'}
          onPageComplete={onPageComplete}
          closeModal={closeModal}
        />
      ),
      nextPageId: 'reset password success',
    },
    'reset password success': {
      element: (
        <AuthenticationResetPasswordSuccessPage
          pageId={'reset password success'}
          onPageComplete={onPageComplete}
        />
      ),
      prevPageId: 'reset password',
    },
    'registration end': {
      element: (
        <AuthenticationFlowCompletePage
          pageId={'registration end'}
          onPageComplete={onPageComplete}
          closeModal={closeModal}
        />
      ),
    },
  };

  const [currentPage, setCurrentPage] = useState<PageIdData>(
    authenticationFlowPageIdMap[`${startPage}`],
  );

  const wrapperPadding = currentPage.prevPageId ? '16px' : '32px';

  const changePages = (newPageId: AuthenticationFlowPageId) => {
    setCurrentPage(authenticationFlowPageIdMap[`${newPageId}`]);
  };

  return (
    <Box
      p={wrapperPadding}
      sx={{
        overflowX: 'hidden',
        '& .notransition': { transition: 'none !important' },
      }}
    >
      {currentPage.currPageId !== 'registration start' && (
        <AuthenticationNavbar
          goBack={() => goBack(currentPage)}
          closeModal={closeModal}
          prevPageId={currentPage.prevPageId}
        />
      )}

      <Box
        display={'flex'}
        flexDirection={'row'}
        width={'100%'}
        maxWidth={'826px'}
        justifyContent={'center'}
        height={'unset'}
      >
        {currentPage.element}
      </Box>
    </Box>
  );
}
